// widget/src/hooks/useSyncPageContext.ts
//
// Syncs page navigation to the server in real-time. Without this, the
// chat_sessions row only updates when the customer sends a chat message
// (via the /api/chat/message ctx overlay). For:
//   - Pearl's get_customer_context tool to see the freshest page on her
//     NEXT turn (not the page they were on N turns ago),
//   - Proactive triggers (cart_abandon, exit_intent, etc.) to fire on the
//     RIGHT page,
//   - The staff dashboard to reflect what the customer is looking at now,
// we POST a `page_view` event to /api/chat/context on every navigation.
//
// Phase 4: the /api/chat/context response carries a server-gated `proactive`
// nudge (the restraint ledger — caps/cooldowns/opt-out — is applied server-side;
// at most one comes back). We surface it via onProactive so the widget renders
// it through the SAME preview-chips strip the opener system uses. We also emit a
// product_dwell signal so the hesitation engine has a cue. Inert until
// proactive_enabled is flipped on server-side.
//
// Pairs with usePageContext (which detects in-widget) — that hook keeps the
// UI fresh; this hook keeps the SERVER fresh.

import { useEffect, useRef } from "react"
import type { PageContext } from "../types"

export interface ProactiveNudge {
  triggerType: string
  message: string
  productHandle: string | null
}

interface ContextEvent {
  type: string
  timestamp: number
  data: Record<string, unknown>
}

export function useSyncPageContext(
  apiBaseUrl: string,
  sessionToken: string,
  pageContext: PageContext,
  onProactive?: (nudge: ProactiveNudge) => void,
): void {
  // Track the last path we've already synced so a re-render with the same
  // ctx doesn't re-POST. usePageContext can re-render on cart-fetch updates
  // even when the path hasn't changed.
  const lastSyncedRef = useRef<string | null>(null)
  // Same idea but for the cart payload — re-POST only when the cart actually
  // mutated. We hash (items, total) into a fingerprint string.
  const lastCartRef = useRef<string | null>(null)
  // Latest onProactive in a ref so the POST helper isn't a dependency (avoids
  // re-POSTing just because the parent re-rendered the callback).
  const onProactiveRef = useRef(onProactive)
  onProactiveRef.current = onProactive

  // Shared POST → parses the server-gated proactive nudge from the response.
  function postContext(events: ContextEvent[]) {
    fetch(`${apiBaseUrl}/api/chat/context`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionToken, events }),
      keepalive: true,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { proactive?: ProactiveNudge } | null) => {
        if (d?.proactive?.message && onProactiveRef.current) {
          onProactiveRef.current(d.proactive)
        }
      })
      .catch(() => {
        // Silent — non-critical sync. The next /api/chat/message ctx overlay
        // is the backstop, and the chat keeps working regardless.
      })
  }

  // ── Page sync ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!sessionToken || !apiBaseUrl) return
    const path = pageContext.currentPage ?? ""
    if (!path) return
    // Combine path + handle so navigating between two product pages still
    // triggers a sync even if the path normalization happens to collide.
    const key = `${path}|${pageContext.productHandle ?? ""}`
    if (key === lastSyncedRef.current) return
    lastSyncedRef.current = key

    postContext([
      {
        type: "page_view",
        timestamp: Date.now(),
        data: {
          url: path,
          title: typeof document !== "undefined" ? document.title : undefined,
          product_handle: pageContext.productHandle,
        },
      },
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, sessionToken, pageContext.currentPage, pageContext.productHandle])

  // ── Cart sync ────────────────────────────────────────────────────────
  // When usePageContext sees a cart mutation (via its fetch monkeypatch on
  // /cart/add.js etc.), cartItems / cartTotal change here. POST a cart_update
  // event so chat_sessions.cart_contents + cart_total reflect reality — the
  // dashboard's copilot reads those fields and re-renders live thanks to the
  // chat_sessions UPDATE realtime subscription on the server side.
  useEffect(() => {
    if (!sessionToken || !apiBaseUrl) return
    const items = pageContext.cartItems
    if (!items) return // baseline not loaded yet
    const total = pageContext.cartTotal ?? 0
    // Fingerprint: count + total + first-3 variant ids. Cheap and stable.
    const fp = `${items.length}|${total.toFixed(2)}|${items
      .slice(0, 3)
      .map((i) => `${i.variantId}x${i.quantity}`)
      .join(",")}`
    if (fp === lastCartRef.current) return
    lastCartRef.current = fp

    postContext([
      { type: "cart_update", timestamp: Date.now(), data: { items, total } },
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, sessionToken, pageContext.cartItems, pageContext.cartTotal])

  // ── Dwell signal ──────────────────────────────────────────────────────
  // On a product page, after a dwell delay, emit a product_dwell signal so the
  // hesitation engine has a cue. The server gate (caps/cooldowns/opt-out + the
  // proactive_enabled flag) decides whether anything actually fires — this just
  // gives it the signal. One dwell emit per page (reset on navigation).
  useEffect(() => {
    if (!sessionToken || !apiBaseUrl) return
    const handle = pageContext.productHandle
    if (!handle) return
    const timer = window.setTimeout(() => {
      postContext([
        {
          // The engine's product_dwell trigger matches on event type
          // "product_view" (not "product_dwell") + data.dwell_ms.
          type: "product_view",
          timestamp: Date.now(),
          data: {
            url: pageContext.currentPage ?? "",
            product_handle: handle,
            dwell_ms: 35_000,
          },
        },
      ])
    }, 35_000)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, sessionToken, pageContext.productHandle])

  // Session-wide proactive suppression — once the customer dismissed a nudge
  // (or said "stop"), don't keep emitting signals that the server would gate
  // anyway. The server ledger is still authoritative; this just avoids noise.
  function proactiveOff(): boolean {
    try {
      return sessionStorage.getItem("pearl-proactive-dismissed") === "1"
    } catch {
      return false
    }
  }

  // ── Scroll depth ──────────────────────────────────────────────────────
  // Fire ONCE when the customer scrolls past 75% of the page — the "read most
  // of it" signal the scroll_depth trigger matches on. Reset per page.
  useEffect(() => {
    if (!sessionToken || !apiBaseUrl || typeof window === "undefined") return
    let fired = false
    function onScroll() {
      if (fired || proactiveOff()) return
      const doc = document.documentElement
      const max = doc.scrollHeight - window.innerHeight
      const pct = max > 0 ? (window.scrollY / max) * 100 : 0
      if (pct >= 75) {
        fired = true
        window.removeEventListener("scroll", onScroll)
        postContext([
          {
            type: "scroll_depth",
            timestamp: Date.now(),
            data: { depth: Math.round(pct), url: pageContext.currentPage ?? "" },
          },
        ])
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, sessionToken, pageContext.currentPage])

  // ── Exit intent (desktop) ─────────────────────────────────────────────
  // Mouse leaving the viewport toward the TOP = likely about to close the tab.
  // Once per page; desktop only (no reliable mobile equivalent).
  useEffect(() => {
    if (!sessionToken || !apiBaseUrl || typeof window === "undefined") return
    if (window.matchMedia?.("(max-width: 767px)").matches) return
    let fired = false
    function onLeave(e: MouseEvent) {
      if (fired || proactiveOff()) return
      if (e.clientY <= 0 && !e.relatedTarget) {
        fired = true
        document.removeEventListener("mouseout", onLeave)
        postContext([
          {
            type: "exit_intent",
            timestamp: Date.now(),
            data: { url: pageContext.currentPage ?? "" },
          },
        ])
      }
    }
    document.addEventListener("mouseout", onLeave)
    return () => document.removeEventListener("mouseout", onLeave)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, sessionToken, pageContext.currentPage])

  // ── Idle ──────────────────────────────────────────────────────────────
  // After ~45s with no pointer/key/scroll activity, emit one idle signal. The
  // server uses it for time_on_page, idle_reengage, AND cart_abandon (when the
  // session has cart contents). Resets on activity; fires once per page.
  useEffect(() => {
    if (!sessionToken || !apiBaseUrl || typeof window === "undefined") return
    const IDLE_MS = 45_000
    let timer: number | undefined
    let fired = false
    const start = Date.now()
    function fire() {
      if (fired || proactiveOff()) return
      fired = true
      postContext([
        {
          type: "idle",
          timestamp: Date.now(),
          data: {
            idle_ms: IDLE_MS,
            seconds: Math.round((Date.now() - start) / 1000),
            url: pageContext.currentPage ?? "",
          },
        },
      ])
    }
    function reset() {
      if (fired) return
      window.clearTimeout(timer)
      timer = window.setTimeout(fire, IDLE_MS)
    }
    const evs = ["mousemove", "keydown", "scroll", "touchstart", "click"]
    evs.forEach((e) => window.addEventListener(e, reset, { passive: true }))
    reset()
    return () => {
      window.clearTimeout(timer)
      evs.forEach((e) => window.removeEventListener(e, reset))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, sessionToken, pageContext.currentPage])
}
